import { z } from 'zod';
import { PaginationOrderDirectionEnum } from '@terra/shared-dto-types';

export const InternalNetworkAssetKindSchema = z.enum([
  'UNKNOWN',
  'COMPUTER',
  'SERVER',
  'DOMAIN_CONTROLLER',
  'NETWORK_DEVICE',
  'APPLIANCE',
]);

export const InternalNetworkAssetPresenceStatusSchema = z.enum([
  'UNKNOWN',
  'ONLINE',
  'OFFLINE',
]);

export const InternalNetworkAssetResolutionStateSchema = z.enum([
  'UNRESOLVED',
  'RESOLVED',
  'CONFLICTED',
  'MERGED',
]);

export const InternalNetworkAssetIdentifierSchema = z.object({
  id: z.string(),
  type: z.enum([
    'IP',
    'HOSTNAME',
    'FQDN',
    'NETBIOS',
    'MAC',
    'AD_OBJECT_ID',
    'AGENT_ID',
  ]),
  rawValue: z.string(),
  normalizedValue: z.string(),
  source: z.enum(['NMAP', 'AGENT_PRESENCE', 'BLOODHOUND', 'DNS']),
  confidence: z.number().int().min(0).max(100),
  firstObservedAt: z.iso.datetime(),
  lastObservedAt: z.iso.datetime(),
  isActive: z.boolean(),
});

export const InternalNetworkAssetAddressSchema = z.object({
  id: z.string(),
  address: z.string(),
  segmentId: z.string().nullable(),
  interfaceId: z.string().nullable(),
  firstObservedAt: z.iso.datetime(),
  lastObservedAt: z.iso.datetime(),
  isActive: z.boolean(),
});

export const InternalNetworkAssetServiceSchema = z.object({
  id: z.string(),
  addressAssignmentId: z.string().nullable(),
  protocol: z.string(),
  port: z.number().int(),
  state: z.enum(['OPEN', 'CLOSED', 'FILTERED', 'UNKNOWN']),
  service: z.string().nullable(),
  product: z.string().nullable(),
  version: z.string().nullable(),
  source: z.enum(['NMAP', 'AGENT_PRESENCE', 'BLOODHOUND', 'DNS']),
  lastObservedAt: z.iso.datetime(),
  staleAt: z.iso.datetime().nullable(),
});

export const InternalNetworkAssetSegmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  cidrs: z.array(z.string()),
  source: z.enum(['TUNNEL_ROUTE', 'NMAP', 'MANUAL']),
  confidence: z.number().int().min(0).max(100),
  lastObservedAt: z.iso.datetime(),
});

export const InternalNetworkAssetResolutionIssueSchema = z.object({
  id: z.string(),
  kind: z.enum([
    'MULTIPLE_CANDIDATES',
    'CONFLICTING_IDENTIFIERS',
    'DUPLICATE_AUTHORITATIVE_IDENTIFIER',
    'OVERLAPPING_SEGMENT',
  ]),
  status: z.enum(['OPEN', 'RESOLVED', 'DISMISSED']),
  candidateAssetIds: z.array(z.string()),
  createdAt: z.iso.datetime(),
});

export const InternalNetworkAssetSourceFreshnessSchema = z.object({
  source: z.enum(['NMAP', 'AGENT_PRESENCE', 'BLOODHOUND', 'DNS']),
  lastObservedAt: z.iso.datetime(),
});

export const InternalNetworkAssetSummarySchema = z.object({
  id: z.string(),
  displayName: z.string(),
  assetKind: InternalNetworkAssetKindSchema,
  presenceStatus: InternalNetworkAssetPresenceStatusSchema,
  resolutionState: InternalNetworkAssetResolutionStateSchema,
  firstObservedAt: z.iso.datetime(),
  lastObservedAt: z.iso.datetime(),
  primaryAddress: z.string().nullable(),
  primaryHostname: z.string().nullable(),
  identifierCount: z.number().int().nonnegative(),
  openServiceCount: z.number().int().nonnegative(),
  conflictCount: z.number().int().nonnegative(),
  adLinked: z.boolean(),
});

export const InternalNetworkAssetDetailSchema =
  InternalNetworkAssetSummarySchema.extend({
    identifiers: z.array(InternalNetworkAssetIdentifierSchema),
    addresses: z.array(InternalNetworkAssetAddressSchema),
    services: z.array(InternalNetworkAssetServiceSchema),
    segments: z.array(InternalNetworkAssetSegmentSchema),
    resolutionIssues: z.array(InternalNetworkAssetResolutionIssueSchema),
    sourceFreshness: z.array(InternalNetworkAssetSourceFreshnessSchema),
    signalIds: z.array(z.string()),
    principalEntityIds: z.array(z.string()),
    adNodeId: z.string().nullable(),
    mergedIntoAssetId: z.string().nullable(),
    latestReversibleMergeEventId: z.string().nullable(),
  });

export const INTERNAL_NETWORK_ASSET_LIST_SORT_FIELDS = [
  'displayName',
  'assetKind',
  'lastObservedAt',
] as const;

export const INTERNAL_NETWORK_ASSET_LIST_GROUP_BY_FIELDS = [
  'assetKind',
] as const;

export const InternalNetworkAssetListOrderBySchema = z.object({
  field: z.enum(INTERNAL_NETWORK_ASSET_LIST_SORT_FIELDS),
  order: PaginationOrderDirectionEnum,
});

export const InternalNetworkAssetListGroupSchema = z.object({
  key: z.string().nullable(),
  count: z.number().int().nonnegative(),
});

export const InternalNetworkAssetListQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  orderBy: InternalNetworkAssetListOrderBySchema.array().max(1).optional(),
  groupBy: z.enum(INTERNAL_NETWORK_ASSET_LIST_GROUP_BY_FIELDS).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  search: z.string().trim().min(1).optional(),
  assetKind: InternalNetworkAssetKindSchema.optional(),
  presenceStatus: InternalNetworkAssetPresenceStatusSchema.optional(),
  resolutionState: InternalNetworkAssetResolutionStateSchema.optional(),
  segmentId: z.string().optional(),
  port: z.coerce.number().int().min(1).max(65_535).optional(),
  service: z.string().trim().min(1).optional(),
  source: z.enum(['NMAP', 'AGENT_PRESENCE', 'BLOODHOUND', 'DNS']).optional(),
  observedAfter: z.coerce.date().optional(),
  adLinked: z
    .union([
      z.boolean(),
      z.enum(['true', 'false']).transform((value) => value === 'true'),
    ])
    .optional(),
});

export const InternalNetworkAssetListResponseSchema = z.object({
  items: z.array(InternalNetworkAssetSummarySchema),
  total: z.number().int().nonnegative(),
  truncated: z.boolean(),
  nextCursor: z.string().nullable(),
  groups: InternalNetworkAssetListGroupSchema.array().optional(),
});

export const InternalNetworkAssetRelationshipSchema = z.object({
  type: z.enum(['AD_ENTITY', 'PRINCIPAL', 'SEGMENT', 'SIGNAL']),
  id: z.string(),
  label: z.string(),
  role: z.string().nullable(),
});

export const InternalNetworkAssetRelationshipsResponseSchema = z.object({
  items: z.array(InternalNetworkAssetRelationshipSchema),
  total: z.number().int().nonnegative(),
  truncated: z.boolean(),
});

export const InternalNetworkAssetMergeResultSchema = z.object({
  eventId: z.string(),
  sourceAssetId: z.string(),
  targetAssetId: z.string(),
  action: z.enum(['MERGED', 'REVERSED']),
});

export const InternalNetworkAssetResolutionResultSchema = z.object({
  issueId: z.string(),
  provisionalAssetId: z.string(),
  status: z.enum(['RESOLVED', 'DISMISSED']),
  action: z.enum(['DISMISSED', 'KEEP_SEPARATE']),
});

export type InternalNetworkAssetSummaryDto = z.infer<
  typeof InternalNetworkAssetSummarySchema
>;
export type InternalNetworkAssetDetailDto = z.infer<
  typeof InternalNetworkAssetDetailSchema
>;
export type InternalNetworkAssetListQueryDto = z.infer<
  typeof InternalNetworkAssetListQuerySchema
>;
export type InternalNetworkAssetListOrderByDto = z.infer<
  typeof InternalNetworkAssetListOrderBySchema
>;
export type InternalNetworkAssetListSortField =
  (typeof INTERNAL_NETWORK_ASSET_LIST_SORT_FIELDS)[number];
export type InternalNetworkAssetListResponseDto = z.infer<
  typeof InternalNetworkAssetListResponseSchema
>;
export type InternalNetworkAssetRelationshipsResponseDto = z.infer<
  typeof InternalNetworkAssetRelationshipsResponseSchema
>;
export type InternalNetworkAssetMergeResultDto = z.infer<
  typeof InternalNetworkAssetMergeResultSchema
>;
export type InternalNetworkAssetResolutionResultDto = z.infer<
  typeof InternalNetworkAssetResolutionResultSchema
>;
